import { BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { useStallRecommendations } from "@/lib/scenarios";

/**
 * Stall screen — shown on the Skills home when nothing actionable
 * remains in the journal. With the new prerequisite-chain lock model
 * this is a degenerate state: it only fires if every scenario is
 * LOCKED, which means the seed data has no foundational entry-point.
 *
 * Kept as a safety net so we never render a fully-empty Skills home,
 * but the stall recommendation list is intentionally a no-op shim
 * since the SOFT_LOCKED-era "fill these word buckets" suggestions
 * stopped making sense once we dropped the coverage gate.
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `ScenariosStallScreen`
 * lines ~7–137.
 */
export function StallScreen() {
  const t = useT();
  const stall = useStallRecommendations();

  return (
    <div className="flex flex-col gap-5 pb-8">
      <div
        className="rounded-[var(--r-lg)] border-l-[3px] px-5 py-4"
        style={{ background: "var(--scen-bg)", borderLeftColor: "var(--scen-fg)" }}
      >
        <div
          className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/70 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider"
          style={{ color: "var(--scen-fg)" }}
        >
          ⏸ {t("skills.tag_pause")}
        </div>
        <h2 className="text-[20px] font-bold leading-tight tracking-tight" style={{ color: "var(--scen-ink)" }}>
          {t("skills.stall_title")}
        </h2>
        <p className="mt-2 text-[13px] leading-[1.5]" style={{ color: "var(--scen-fg)" }}>
          {t("skills.stall_subtitle")}
        </p>
      </div>

      {stall.isLoading ? (
        <div className="flex justify-center py-6 text-[12px] text-text-3">…</div>
      ) : stall.data && stall.data.domains.length > 0 ? (
        <div className="flex flex-col gap-2">
          {stall.data.domains.map((d) => {
            const unlocksCount = d.unlocksScenarios.length;
            const unlocksLabel =
              unlocksCount === 1
                ? t("skills.stall_domain_unlocks_one")
                : unlocksCount < 5
                  ? t("skills.stall_domain_unlocks_few")
                  : t("skills.stall_domain_unlocks_many");
            return (
              <div
                key={d.domain}
                className="rounded-[var(--r-md)] border bg-surface px-4 py-3"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center justify-center rounded px-2 py-0.5 font-mono text-[11px] font-bold"
                      style={{ background: "var(--surface-2)", color: "var(--text-2)" }}
                    >
                      {d.level}
                    </span>
                    <span className="text-[11px] uppercase tracking-wider text-text-3">
                      {t("skills.stall_domain_unlocks", {
                        n: String(unlocksCount),
                        label: unlocksLabel,
                      })}
                    </span>
                  </div>
                  {/* Route to the existing study flow. We don't have a
                      level-filtered review queue today — the Open mode's
                      Study page picks up due cards anyway. Phase 7's
                      "filter by level" is a small enhancement layered
                      on top once we have the level-scoped queue. */}
                  <Link
                    to="/study"
                    className="inline-flex items-center gap-1 rounded-[var(--r-sm)] px-2.5 py-1 font-mono text-[11px] font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ background: "var(--scen-solid)" }}
                  >
                    <BookOpen className="size-3" /> {t("skills.stall_domain_cta")}
                  </Link>
                </div>
                <div className="mt-1.5 font-mono text-[11px] text-text-3">
                  {t("skills.stall_domain_have", { have: String(d.have), need: String(d.need) })}
                </div>
                <div className="mt-1.5 h-1 w-full rounded-full" style={{ background: "var(--surface-2)" }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${d.need > 0 ? Math.round((d.have / d.need) * 100) : 0}%`,
                      background: "var(--scen-solid)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-4 py-3 text-center text-[12px] text-text-3">
          —
        </div>
      )}

      <div className="text-center">
        <Link to="/settings" className="text-[12px] text-text-3 underline-offset-2 hover:text-text hover:underline">
          {t("skills.stall_switch_mode")}
        </Link>
      </div>
    </div>
  );
}
